import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Heart, Shield, Zap, Clock, CheckCircle, Activity, Sparkles, Flame, Home, Car } from "lucide-react";

const LOGO_MASSAGE = "https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/bkt4vijm_IMG_5566.png";

const MassagePage = () => {
  const massageTypes = [
    {
      title: "Swedish Massage",
      price60: "$75",
      price90: "$105",
      description: "A calming, full-body experience using long, flowing strokes to melt away stress, improve circulation, and restore total body balance. Ideal for relaxation and mental reset."
    },
    {
      title: "Deep Tissue Massage",
      price60: "$85",
      price90: "$120",
      description: "A therapeutic treatment targeting deeper muscle layers to release chronic tension, break up adhesions, and relieve persistent discomfort while maintaining comfort."
    },
    {
      title: "Sports Massage",
      price60: "$80",
      price90: "$115",
      description: "Designed for active lifestyles and athletes. Enhances performance, accelerates recovery, reduces muscle soreness, and increases flexibility through targeted techniques."
    },
    {
      title: "Prenatal Massage",
      price60: "$80",
      price90: "$110",
      description: "A nurturing session tailored for expectant mothers to ease lower back pain, reduce swelling, and promote relaxation and restful sleep. (Second & third trimester only.)"
    }
  ];

  const addOns = [
    { name: "Aromatherapy", price: "$10" },
    { name: "Hot Stone Therapy", price: "$20" }
  ];

  const packages = [
    { name: "3-Pack (60 Min)", price: "$210", savings: "Save $15" },
    { name: "5-Pack (60 Min)", price: "$325", savings: "Save $50" },
    { name: "3-Pack (90 Min)", price: "$300", savings: "Save $15" },
    { name: "5-Pack (90 Min)", price: "$475", savings: "Save $75" }
  ];

  const membership = {
    name: "Relax & Save Plan",
    price: "$65/month",
    features: [
      "One 60-minute Swedish massage per month",
      "10% off all additional services",
      "Rollover unused session (1 month max)"
    ]
  };

  const benefits = [
    { icon: <Activity className="w-6 h-6" />, title: "Faster Recovery", desc: "Reduce muscle soreness by up to 50%" },
    { icon: <Shield className="w-6 h-6" />, title: "Injury Prevention", desc: "Address tight spots before they become problems" },
    { icon: <Zap className="w-6 h-6" />, title: "Better Performance", desc: "Improved range of motion and flexibility" },
    { icon: <Heart className="w-6 h-6" />, title: "Stay Game-Ready", desc: "Keep your body performing at peak levels" }
  ];

  return (
    <div className="min-h-screen" data-testid="massage-page">
      {/* Hero */}
      <section className="hero-bg py-24 relative">
        <div className="absolute inset-0 athletic-pattern"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="max-w-3xl">
              <p className="text-[#E10600] font-bold uppercase tracking-widest mb-4">Sports Recovery</p>
              <h1 className="font-display text-4xl sm:text-5xl md:text-7xl text-white mb-4">
                MASSAGE<br /><span className="text-[#E10600]">THERAPY</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-400 mb-8">
                This isn't a spa day. This is recovery that keeps you on the field and off the bench.
              </p>
              <Link to="/booking?category=massage" data-testid="massage-hero-book">
                <Button className="cta-btn-primary text-base md:text-lg">
                  Book Recovery Session
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="hidden md:block">
              <img src={LOGO_MASSAGE} alt="Top Notch Massage" className="h-48 lg:h-64 w-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Mobile/In-Home Service Notice */}
      <section className="py-12 bg-[#111111] border-y border-[#2A2A2A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Home className="w-8 h-8 text-[#E10600]" />
              <h3 className="font-display text-2xl md:text-3xl text-white">IN-HOME MASSAGE SERVICES</h3>
            </div>
            <p className="text-gray-400">We travel to you for your convenience</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-[#1a1a1a] border-[#2A2A2A]">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#E10600]/20 flex items-center justify-center flex-shrink-0">
                    <Car className="w-6 h-6 text-[#E10600]" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">Travel Fee</p>
                    <p className="text-[#E10600] font-display text-2xl">$10</p>
                    <p className="text-gray-400 text-sm">Base travel fee</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#1a1a1a] border-[#2A2A2A]">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#E10600]/20 flex items-center justify-center flex-shrink-0">
                    <Activity className="w-6 h-6 text-[#E10600]" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">Additional Mileage</p>
                    <p className="text-[#E10600] font-display text-2xl">$0.50<span className="text-base text-gray-400">/mile</span></p>
                    <p className="text-gray-400 text-sm">Outside 10 miles of Gloucester Township, NJ</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl md:text-4xl text-white text-center mb-12">
            ATHLETE-FOCUSED <span className="text-[#E10600]">RECOVERY</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="feature-card" data-testid={`benefit-card-${index}`}>
                <div className="feature-icon mx-auto">
                  {benefit.icon}
                </div>
                <h3 className="text-white font-bold mb-2">{benefit.title}</h3>
                <p className="text-gray-400 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Massage Types with Pricing Table */}
      <section className="py-20 bg-[#111111]" data-testid="massage-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl md:text-4xl text-white text-center mb-4">MASSAGE THERAPY PRICING</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">Professional therapeutic massage tailored to your needs</p>
          
          {/* Pricing Table Header */}
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              <div className="grid grid-cols-4 gap-4 mb-4 px-4">
                <div className="text-gray-400 font-semibold">Massage Type</div>
                <div className="text-center text-gray-400 font-semibold">60 Min</div>
                <div className="text-center text-gray-400 font-semibold">90 Min</div>
                <div></div>
              </div>
              
              {massageTypes.map((massage, index) => (
                <Card key={index} className="bg-[#1a1a1a] border-[#2A2A2A] mb-4 hover:border-[#E10600] transition-all" data-testid={`massage-type-${index}`}>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-4 gap-4 items-center">
                      <div>
                        <h3 className="font-display text-xl text-white">{massage.title}</h3>
                      </div>
                      <div className="text-center">
                        <span className="text-[#E10600] font-bold text-xl">{massage.price60}</span>
                      </div>
                      <div className="text-center">
                        <span className="text-[#E10600] font-bold text-xl">{massage.price90}</span>
                      </div>
                      <div className="text-right">
                        <Link to="/booking?category=massage">
                          <Button size="sm" className="bg-[#E10600] hover:bg-[#B80500]">Book</Button>
                        </Link>
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm mt-4">{massage.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Add-Ons */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl text-white text-center mb-8">ADD-ONS</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {addOns.map((addon, index) => (
              <Card key={index} className="bg-[#111111] border-[#2A2A2A] hover:border-[#E10600] transition-all">
                <CardContent className="p-6 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-[#E10600]" />
                    <span className="text-white font-medium">{addon.name}</span>
                  </div>
                  <span className="text-[#E10600] font-bold text-lg">{addon.price}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Package Deals */}
      <section className="py-20 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl md:text-4xl text-white text-center mb-4">PACKAGE DEALS</h2>
          <p className="text-gray-400 text-center mb-12">Save more when you commit to your recovery</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg, index) => (
              <Card key={index} className="bg-[#1a1a1a] border-[#2A2A2A] hover:border-[#E10600] transition-all text-center" data-testid={`package-${index}`}>
                <CardContent className="p-6">
                  <h3 className="font-display text-xl text-white mb-2">{pkg.name}</h3>
                  <p className="text-[#E10600] font-bold text-3xl mb-2">{pkg.price}</p>
                  <span className="inline-block bg-[#E10600]/20 text-[#E10600] text-sm px-3 py-1 rounded-full">{pkg.savings}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Monthly Membership */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <Card className="bg-gradient-to-br from-[#E10600]/20 to-[#111111] border-[#E10600]" data-testid="membership-card">
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 rounded-full bg-[#E10600] flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="font-display text-3xl text-white">MONTHLY MEMBERSHIP</CardTitle>
                <p className="text-gray-400">{membership.name}</p>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-[#E10600] font-bold text-4xl mb-6">{membership.price}</p>
                <ul className="space-y-3 text-left max-w-sm mx-auto mb-8">
                  {membership.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#E10600] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" data-testid="membership-inquire">
                  <Button className="cta-btn-primary w-full max-w-xs">
                    Inquire About Membership
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#E10600]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">
            RECOVER LIKE A PRO
          </h2>
          <p className="text-white/80 mb-8">
            Your body is your weapon. Keep it in peak condition.
          </p>
          <Link to="/booking?category=massage" data-testid="massage-cta-book">
            <Button className="bg-white text-[#0A0A0A] hover:bg-gray-100 font-bold text-lg px-8 py-6">
              BOOK RECOVERY SESSION
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MassagePage;
